import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  GripVertical,
  Eye,
  MessageSquare,
  FileDown,
  Clock,
  CheckCircle,
  Package,
  Truck,
  FileText,
  Printer,
  Search,
  X
} from "lucide-react";
import OrderItemDetail from "@/components/OrderItemDetail";

const ORDER_STATUSES = [
  { value: "order_confirmed", label: "Order Confirmed", color: "bg-blue-500" },
  { value: "material_planning", label: "Material Planning", color: "bg-purple-500" },
  { value: "cutting_scheduled", label: "Cutting Scheduled", color: "bg-indigo-500" },
  { value: "cutting_in_progress", label: "Cutting In Progress", color: "bg-yellow-500" },
  { value: "qc_check", label: "QC Check", color: "bg-orange-500" },
  { value: "packed", label: "Packed", color: "bg-teal-500" },
  { value: "dispatched", label: "Dispatched", color: "bg-cyan-500" },
  { value: "delivered", label: "Delivered", color: "bg-green-500" }
];

const getStatusLabel = (status) => {
  const found = ORDER_STATUSES.find(s => s.value === status);
  return found?.label || status;
};

const getStatusColor = (status) => {
  const found = ORDER_STATUSES.find(s => s.value === status);
  return found?.color || "bg-slate-500";
};

export default function OrdersKanbanPage() {
  const { hasRole, isCustomer } = useAuth();
  const [kanbanData, setKanbanData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [erpSoNumber, setErpSoNumber] = useState("");
  const [erpInvoiceNumber, setErpInvoiceNumber] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [statusNotes, setStatusNotes] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const canManageOrders = hasRole("super_admin", "sales_manager", "production_manager");
  const [kanbanSearch, setKanbanSearch] = useState("");
  const [customerFilter, setCustomerFilter] = useState("all");

  const allCustomers = useMemo(() => {
    const names = new Set();
    Object.values(kanbanData).forEach(col => col.forEach(o => names.add(o.customer_name)));
    return ["all", ...Array.from(names).sort()];
  }, [kanbanData]);

  const filteredKanbanData = useMemo(() => {
    const term = kanbanSearch.toLowerCase().trim();
    const result = {};
    Object.entries(kanbanData).forEach(([status, orders]) => {
      result[status] = orders.filter(o => {
        const matchesSearch = !term ||
          o.order_number?.toLowerCase().includes(term) ||
          o.customer_name?.toLowerCase().includes(term) ||
          o.company_name?.toLowerCase().includes(term);
        const matchesCustomer = customerFilter === "all" || o.customer_name === customerFilter;
        return matchesSearch && matchesCustomer;
      });
    });
    return result;
  }, [kanbanData, kanbanSearch, customerFilter]);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      if (canManageOrders) {
        const res = await api.get("/orders/kanban");
        setKanbanData(res.data);
      } else {
        const res = await api.get("/orders");
        const grouped = {};
        ORDER_STATUSES.forEach(s => grouped[s.value] = []);
        res.data.forEach(order => {
          if (grouped[order.status]) {
            grouped[order.status].push(order);
          }
        });
        setKanbanData(grouped);
      }
    } catch (error) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, order) => {
    if (!canManageOrders) return;
    setDraggedOrder(order);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    if (!canManageOrders) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!canManageOrders || !draggedOrder) return;
    
    if (draggedOrder.status === newStatus) {
      setDraggedOrder(null);
      return;
    }

    if (newStatus === "dispatched" && !draggedOrder.erp_invoice_number) {
      toast.error("ERP Invoice No. is required before dispatching. Open order details to provide it.");
      setDraggedOrder(null);
      return;
    }

    try {
      await api.put(`/orders/${draggedOrder.id}/status`, {
        status: newStatus,
        notes: `Moved to ${getStatusLabel(newStatus)}`
      });
      toast.success(`Order moved to ${getStatusLabel(newStatus)}`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setDraggedOrder(null);
    }
  };

  const viewOrderDetail = async (order) => {
    try {
      const res = await api.get(`/orders/${order.id}`);
      setSelectedOrder(res.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error("Failed to load order details");
    }
  };

  const addComment = async () => {
    if (!newComment.trim() || !selectedOrder) return;
    setAddingComment(true);

    try {
      await api.post(`/orders/${selectedOrder.id}/comment`, {
        comment: newComment
      });
      toast.success("Comment added");
      setNewComment("");
      
      // Refresh order details
      const res = await api.get(`/orders/${selectedOrder.id}`);
      setSelectedOrder(res.data);
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setAddingComment(false);
    }
  };

  const getNextStatuses = (currentStatus) => {
    const idx = ORDER_STATUSES.findIndex(s => s.value === currentStatus);
    if (idx < 0 || idx >= ORDER_STATUSES.length - 1) return [];
    return ORDER_STATUSES.slice(idx + 1);
  };

  const updateOrderStatus = async () => {
    if (!newStatus || !selectedOrder) return;
    if (newStatus === "dispatched" && !erpInvoiceNumber && !selectedOrder.erp_invoice_number) {
      toast.error("ERP Invoice No. is required before dispatching");
      return;
    }
    setUpdatingStatus(true);
    try {
      const payload = { status: newStatus, notes: statusNotes || null };
      if (erpSoNumber) payload.erp_so_number = erpSoNumber;
      if (erpInvoiceNumber) payload.erp_invoice_number = erpInvoiceNumber;
      const res = await api.put(`/orders/${selectedOrder.id}/status`, payload);
      toast.success("Order status updated");
      fetchOrders();
      setSelectedOrder(res.data);
      setNewStatus(""); setStatusNotes(""); setErpSoNumber(""); setErpInvoiceNumber("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const downloadChallan = async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}/challan/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `delivery-challan-${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Challan downloaded");
    } catch (error) {
      toast.error("Failed to download challan");
    }
  };

  const printOrder = (order) => {
    const statusLabel = getStatusLabel(order.status);
    const formatCurrency = (v) => `₹${(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN") : "—";

    const itemsHtml = (order.items || []).map((item, i) => {
      const uom = (item.applied_rate_uom || "").toUpperCase();
      const sheets = item.nesting_sheets_used ?? 1;
      const sheetLen = item.product_length_mm || item.nesting_length_used_mm || 1000;
      const netMtr = ((sheets * sheetLen) / 1000).toFixed(3);
      const qty = item.quantity || 1;
      const pieceAreaSqm = ((item.shapes_area_sqmm || 0) / qty / 1_000_000).toFixed(4);
      const matCostPerPc = qty > 0 ? (item.material_cost / qty).toFixed(2) : "0.00";
      const cutRate = item.cutting_rate_per_running_mm || 0;
      const l1l2 = [item.list_1, item.list_2].filter(Boolean).join(" · ");

      return `
        <div style="margin-bottom:20px;border:1px solid #e2e8f0;page-break-inside:avoid">
          <div style="background:#1e293b;color:#fff;padding:8px 12px;font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase">
            Item ${i + 1}: ${item.product_name}${l1l2 ? " · " + l1l2 : ""} &nbsp;|&nbsp; ${item.material_type || "ROLL"}
          </div>
          <div style="padding:12px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;font-size:12px;background:#f8fafc">
            <div><span style="color:#64748b">Design File:</span> <strong>${item.filename || "—"}</strong></div>
            <div><span style="color:#64748b">Quantity:</span> <strong>${item.quantity} pcs</strong></div>
            <div><span style="color:#64748b">Material Width:</span> <strong>${(item.product_width_mm || 0).toLocaleString()} mm</strong></div>
            <div><span style="color:#64748b">Sheets Used:</span> <strong>${sheets}</strong></div>
            <div><span style="color:#64748b">Net Length:</span> <strong>${netMtr} mtr</strong></div>
            <div><span style="color:#64748b">Wastage:</span> <strong>${item.wastage_pct?.toFixed(1) ?? "—"}%</strong></div>
            <div><span style="color:#64748b">Pieces Count:</span> <strong>${item.pieces_count?.toLocaleString() ?? "—"}</strong></div>
            <div><span style="color:#64748b">Cutting Rate:</span> <strong>${cutRate > 0 ? `₹${cutRate.toFixed(2)}/pc` : "—"}</strong></div>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:12px">
            <thead>
              <tr style="background:#334155;color:#fff">
                <th style="padding:6px 10px;text-align:left">Component</th>
                <th style="padding:6px 10px;text-align:right">Qty</th>
                <th style="padding:6px 10px;text-align:right">Per Piece</th>
                <th style="padding:6px 10px;text-align:right">Rate</th>
                <th style="padding:6px 10px;text-align:right">Cost/Piece</th>
                <th style="padding:6px 10px;text-align:right">Total Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr style="border-bottom:1px solid #e2e8f0">
                <td style="padding:6px 10px">${item.component_name || item.filename} (Material)</td>
                <td style="padding:6px 10px;text-align:right">${item.quantity}</td>
                <td style="padding:6px 10px;text-align:right;font-family:monospace">${pieceAreaSqm} sq.m</td>
                <td style="padding:6px 10px;text-align:right;font-family:monospace">₹${item.applied_rate}/${uom === "MTR" ? "mtr" : uom === "SQM" ? "sq.m" : uom.toLowerCase() || "unit"}</td>
                <td style="padding:6px 10px;text-align:right;font-family:monospace">₹${matCostPerPc}</td>
                <td style="padding:6px 10px;text-align:right;font-family:monospace;font-weight:600">${formatCurrency(item.material_cost)}</td>
              </tr>
              <tr style="border-bottom:1px solid #e2e8f0;${cutRate === 0 ? "opacity:0.5" : ""}">
                <td style="padding:6px 10px">${item.component_name || item.filename} (Cutting)</td>
                <td style="padding:6px 10px;text-align:right">${item.quantity}</td>
                <td style="padding:6px 10px;text-align:right;font-family:monospace">${cutRate > 0 ? `₹${cutRate.toFixed(2)}/pc` : "—"}</td>
                <td style="padding:6px 10px;text-align:right;font-family:monospace">${cutRate > 0 ? `₹${cutRate.toFixed(2)}/pc` : "—"}</td>
                <td style="padding:6px 10px;text-align:right;font-family:monospace">${cutRate > 0 ? `₹${cutRate.toFixed(2)}` : "—"}</td>
                <td style="padding:6px 10px;text-align:right;font-family:monospace;font-weight:600">${formatCurrency(item.cutting_cost)}</td>
              </tr>
              <tr style="background:#1e293b;color:#fff">
                <td colspan="5" style="padding:8px 10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px">Total Order Cost</td>
                <td style="padding:8px 10px;text-align:right;font-family:monospace;font-weight:700;color:#fb923c;font-size:14px">${formatCurrency(item.item_total)}</td>
              </tr>
            </tbody>
          </table>
        </div>`;
    }).join("");

    const workflowHtml = (order.workflow_history || []).map(entry => `
      <tr>
        <td style="padding:5px 8px;border-bottom:1px solid #f1f5f9">${getStatusLabel(entry.status)}</td>
        <td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;font-family:monospace">${new Date(entry.timestamp).toLocaleString("en-IN")}</td>
        <td style="padding:5px 8px;border-bottom:1px solid #f1f5f9">${entry.user_name}</td>
        <td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#64748b">${entry.notes || "—"}</td>
      </tr>`).join("");

    const erpBlock = (order.erp_so_number || order.erp_invoice_number) ? `
      <div style="display:flex;gap:16px;margin-bottom:16px">
        ${order.erp_so_number ? `<div style="background:#eff6ff;border:1px solid #bfdbfe;padding:8px 14px;flex:1"><span style="font-size:10px;color:#3b82f6;text-transform:uppercase;display:block">ERP SO No.</span><strong style="font-family:monospace">${order.erp_so_number}</strong></div>` : ""}
        ${order.erp_invoice_number ? `<div style="background:#f0fdf4;border:1px solid #bbf7d0;padding:8px 14px;flex:1"><span style="font-size:10px;color:#16a34a;text-transform:uppercase;display:block">ERP Invoice No.</span><strong style="font-family:monospace">${order.erp_invoice_number}</strong></div>` : ""}
      </div>` : "";

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Order ${order.order_number}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; color: #1e293b; font-size: 13px; }
    @page { margin: 16mm 14mm; size: A4; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <!-- Header -->
  <div style="background:#0f172a;color:#fff;padding:16px 20px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:center">
    <div>
      <div style="font-size:18px;font-weight:700;letter-spacing:2px;text-transform:uppercase">RIMCORE PRECUT</div>
      <div style="color:#94a3b8;font-size:11px;margin-top:2px">Order Confirmation</div>
    </div>
    <div style="text-align:right">
      <div style="font-size:20px;font-weight:700;font-family:monospace">${order.order_number}</div>
      <div style="color:#94a3b8;font-size:11px">Printed: ${new Date().toLocaleString("en-IN")}</div>
    </div>
  </div>

  <!-- Order Info -->
  <div style="display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:12px;margin-bottom:20px">
    <div style="background:#f8fafc;border:1px solid #e2e8f0;padding:10px">
      <div style="font-size:10px;color:#64748b;text-transform:uppercase;margin-bottom:4px">Customer</div>
      <div style="font-weight:600">${order.customer_name}</div>
    </div>
    <div style="background:#f8fafc;border:1px solid #e2e8f0;padding:10px">
      <div style="font-size:10px;color:#64748b;text-transform:uppercase;margin-bottom:4px">Company</div>
      <div style="font-weight:600">${order.company_name}</div>
    </div>
    <div style="background:#f8fafc;border:1px solid #e2e8f0;padding:10px">
      <div style="font-size:10px;color:#64748b;text-transform:uppercase;margin-bottom:4px">Status</div>
      <div style="font-weight:600">${statusLabel}</div>
    </div>
    <div style="background:#f8fafc;border:1px solid #e2e8f0;padding:10px">
      <div style="font-size:10px;color:#64748b;text-transform:uppercase;margin-bottom:4px">Expected Delivery</div>
      <div style="font-weight:600;font-family:monospace">${formatDate(order.expected_delivery_date || order.estimated_delivery_date)}</div>
    </div>
  </div>

  ${erpBlock}

  <!-- Items -->
  ${itemsHtml}

  <!-- Cost Summary -->
  <div style="margin-bottom:20px;border:1px solid #e2e8f0;page-break-inside:avoid">
    <div style="background:#1e293b;color:#fff;padding:8px 12px;font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase">Cost Summary</div>
    <div style="padding:12px">
      <table style="width:100%;font-size:13px">
        <tr><td style="padding:4px 0;color:#64748b">Subtotal</td><td style="text-align:right;font-family:monospace">${formatCurrency(order.subtotal)}</td></tr>
        <tr><td style="padding:4px 0;color:#64748b">Freight</td><td style="text-align:right;font-family:monospace">${formatCurrency(order.freight_cost)}</td></tr>
        <tr style="border-top:2px solid #1e293b">
          <td style="padding:8px 0;font-weight:700;font-size:15px">Total</td>
          <td style="text-align:right;font-family:monospace;font-weight:700;font-size:16px;color:#ea580c">${formatCurrency(order.total)}</td>
        </tr>
      </table>
      <p style="margin-top:8px;font-size:11px;color:#94a3b8">* GST will be Charged Extra as Applicable</p>
    </div>
  </div>

  <!-- Workflow History -->
  <div style="border:1px solid #e2e8f0;page-break-inside:avoid">
    <div style="background:#1e293b;color:#fff;padding:8px 12px;font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase">Workflow History</div>
    <table style="width:100%;border-collapse:collapse;font-size:12px">
      <thead>
        <tr style="background:#f1f5f9">
          <th style="padding:6px 8px;text-align:left;border-bottom:1px solid #e2e8f0">Status</th>
          <th style="padding:6px 8px;text-align:left;border-bottom:1px solid #e2e8f0">Timestamp</th>
          <th style="padding:6px 8px;text-align:left;border-bottom:1px solid #e2e8f0">By</th>
          <th style="padding:6px 8px;text-align:left;border-bottom:1px solid #e2e8f0">Notes</th>
        </tr>
      </thead>
      <tbody>${workflowHtml}</tbody>
    </table>
  </div>

  <div style="margin-top:20px;text-align:center;font-size:11px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:12px">
    RIMCORE PRECUT &mdash; Precision Delivered. Process Simplified.
  </div>
</body>
</html>`;

    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) { toast.error("Please allow popups to print"); return; }
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="orders-kanban-page">
      {/* Header */}
      <div className="border-b border-slate-200 pb-6">
        <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase tracking-tight" data-testid="orders-title">
          Orders
        </h1>
        <p className="text-slate-500 mt-1">
          {canManageOrders ? "Drag and drop orders between stages" : "Track your order status"}
        </p>
      </div>

      {/* Search + Filter Bar */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <Input
            value={kanbanSearch}
            onChange={e => setKanbanSearch(e.target.value)}
            placeholder="Search order # or customer..."
            className="pl-9 h-9 rounded-none text-sm"
          />
          {kanbanSearch && (
            <button onClick={() => setKanbanSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
              <X size={14} />
            </button>
          )}
        </div>
        <Select value={customerFilter} onValueChange={setCustomerFilter}>
          <SelectTrigger className="w-52 h-9 rounded-none text-sm">
            <SelectValue placeholder="All customers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Customers</SelectItem>
            {allCustomers.filter(c => c !== "all").map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(kanbanSearch || customerFilter !== "all") && (
          <button
            onClick={() => { setKanbanSearch(""); setCustomerFilter("all"); }}
            className="text-xs text-slate-500 hover:text-slate-800 underline"
          >Clear filters</button>
        )}
      </div>

      {/* Mobile list view */}
      <div className="md:hidden space-y-2">
        {ORDER_STATUSES.map(status => {
          const orders = filteredKanbanData[status.value] || [];
          if (orders.length === 0) return null;
          return (
            <div key={status.value}>
              <div className={`${status.color} text-white px-3 py-1.5 flex items-center justify-between`}>
                <span className="text-xs font-bold uppercase tracking-wider">{status.label}</span>
                <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded-full">{orders.length}</span>
              </div>
              {orders.map(order => (
                <div key={order.id} className="bg-white border-x border-b border-slate-200 px-4 py-3 active:bg-slate-50"
                  onClick={() => viewOrderDetail(order)}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-xs text-slate-500">{order.order_number}</span>
                    <span className="font-mono text-sm font-bold">₹{order.total?.toLocaleString()}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-900">{order.customer_name}</p>
                  {order.items?.[0] && (
                    <p className="text-xs text-slate-500 truncate mt-0.5">
                      {[order.items[0].product_name, order.items[0].list_1, order.items[0].list_2].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  {order.expected_delivery_date && (
                    <p className="text-[10px] text-slate-400 mt-1">Due: {order.expected_delivery_date}</p>
                  )}
                </div>
              ))}
            </div>
          );
        })}
        {Object.values(filteredKanbanData).every(col => col.length === 0) && (
          <div className="text-center py-12 text-slate-400 text-sm">No orders</div>
        )}
      </div>

      {/* Kanban Board (desktop only) */}
      <div className="hidden md:block overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {ORDER_STATUSES.map((status) => (
            <div
              key={status.value}
              className="w-72 flex-shrink-0"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status.value)}
              data-testid={`kanban-column-${status.value}`}
            >
              {/* Column Header */}
              <div className={`${status.color} text-white p-3 mb-2`}>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-sm uppercase tracking-wider">{status.label}</span>
                  <Badge variant="secondary" className="bg-white/20 text-white border-0">
                    {filteredKanbanData[status.value]?.length || 0}
                  </Badge>
                </div>
              </div>

              {/* Cards Container */}
              <div className="space-y-2 min-h-[200px] bg-slate-100 p-2 rounded-sm">
                {(filteredKanbanData[status.value] || []).map((order) => (
                  <Card
                    key={order.id}
                    className={`cursor-pointer hover:shadow-md transition-shadow bg-white ${
                      canManageOrders ? "cursor-grab active:cursor-grabbing" : ""
                    }`}
                    draggable={canManageOrders}
                    onDragStart={(e) => handleDragStart(e, order)}
                    onClick={() => viewOrderDetail(order)}
                    data-testid={`order-card-${order.id}`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        {canManageOrders && (
                          <GripVertical size={14} className="text-slate-300 mr-1 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-xs text-slate-500">{order.order_number}</p>
                          <p className="font-medium text-sm truncate">{order.customer_name}</p>
                          {order.items?.[0] && (
                            <p className="text-xs text-slate-600 truncate mt-0.5">
                              {[
                                order.items[0].product_name,
                                order.items[0].list_1,
                                order.items[0].list_2,
                              ].filter(Boolean).join(" · ")}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{order.items?.length || 0} item(s)</span>
                        <span className="font-mono">₹{order.total?.toLocaleString()}</span>
                      </div>
                      {order.expected_delivery_date && (
                        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1 text-xs text-slate-400">
                          <Clock size={12} />
                          <span>Due: {order.expected_delivery_date}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {(filteredKanbanData[status.value] || []).length === 0 && (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    No orders
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>{/* end hidden md:block kanban */}

      {/* Order Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="w-full max-w-5xl h-[100dvh] md:h-auto md:max-h-[90vh] overflow-y-auto rounded-none p-0 md:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-2">
              <Package size={20} />
              {selectedOrder?.order_number}
              <button
                onClick={() => printOrder(selectedOrder)}
                className="ml-auto flex items-center gap-1.5 text-xs font-normal normal-case tracking-normal border border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-slate-900 px-2.5 py-1.5"
                title="Print order"
              >
                <Printer size={14} />
                Print
              </button>
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6 mt-4">
              {/* Order Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 p-3">
                  <p className="text-xs text-slate-500 uppercase">Customer</p>
                  <p className="font-medium text-sm mt-1">{selectedOrder.customer_name}</p>
                </div>
                <div className="bg-slate-50 p-3">
                  <p className="text-xs text-slate-500 uppercase">Company</p>
                  <p className="font-medium text-sm mt-1">{selectedOrder.company_name}</p>
                </div>
                <div className="bg-slate-50 p-3">
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                  <p className="font-mono text-sm mt-1">₹{selectedOrder.total.toLocaleString()}</p>
                </div>
                <div className="bg-slate-50 p-3">
                  <p className="text-xs text-slate-500 uppercase">Status</p>
                  <Badge className={`mt-1 ${getStatusColor(selectedOrder.status)} text-white border-0`}>
                    {getStatusLabel(selectedOrder.status)}
                  </Badge>
                </div>
                <div className="bg-slate-50 p-3">
                  <p className="text-xs text-slate-500 uppercase">Est. Delivery</p>
                  <p className="font-mono text-sm mt-1">{selectedOrder.estimated_delivery_date ? new Date(selectedOrder.estimated_delivery_date).toLocaleDateString("en-IN") : "—"}</p>
                </div>
              </div>

              {/* ERP Reference Numbers */}
              {(selectedOrder.erp_so_number || selectedOrder.erp_invoice_number) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedOrder.erp_so_number && (
                    <div className="bg-blue-50 border border-blue-200 p-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <div><span className="text-xs text-blue-500 uppercase tracking-wider block">ERP SO No.</span><span className="font-mono font-medium">{selectedOrder.erp_so_number}</span></div>
                    </div>
                  )}
                  {selectedOrder.erp_invoice_number && (
                    <div className="bg-green-50 border border-green-200 p-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-green-600" />
                      <div><span className="text-xs text-green-500 uppercase tracking-wider block">ERP Invoice No.</span><span className="font-mono font-medium">{selectedOrder.erp_invoice_number}</span></div>
                    </div>
                  )}
                </div>
              )}

              {/* Item Details — Same as Step 3 */}
              {selectedOrder.items.map((item, index) => (
                <OrderItemDetail key={index} item={item} index={index} />
              ))}

              {/* Workflow History */}
              <div className="border border-slate-200 p-4">
                <h3 className="font-heading text-sm uppercase tracking-wider mb-3 pb-2 border-b">Workflow History</h3>
                <div className="space-y-3">
                  {selectedOrder.workflow_history.map((entry, index) => (
                    <div key={index} className="flex gap-3" data-testid={`workflow-entry-${index}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          index === selectedOrder.workflow_history.length - 1
                            ? "bg-orange-500 text-white"
                            : "bg-green-100 text-green-600"
                        }`}>
                          <CheckCircle size={14} />
                        </div>
                        {index < selectedOrder.workflow_history.length - 1 && (
                          <div className="w-0.5 h-6 bg-slate-200 mt-1" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{getStatusLabel(entry.status)}</p>
                        <p className="text-xs text-slate-500">
                          {entry.user_name} • {new Date(entry.timestamp).toLocaleString()}
                        </p>
                        {entry.notes && (
                          <p className="text-xs text-slate-600 mt-1 bg-slate-50 p-2">{entry.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Update + ERP Fields - Admin Only */}
              {canManageOrders && selectedOrder.status !== "delivered" && (
                <div className="border border-slate-200 p-4">
                  <h3 className="font-heading text-sm uppercase tracking-wider mb-4 pb-2 border-b">Update Status & ERP Details</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">New Status</Label>
                      <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger className="rounded-none mt-2" data-testid="kanban-new-status-select"><SelectValue placeholder="Select next status" /></SelectTrigger>
                        <SelectContent>{getNextStatuses(selectedOrder.status).map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">ERP Sales Order No. {!selectedOrder.erp_so_number && "(Optional)"}</Label>
                      <Input
                        value={erpSoNumber}
                        onChange={(e) => setErpSoNumber(e.target.value)}
                        placeholder={selectedOrder.erp_so_number || "Enter ERP SO No."}
                        className="rounded-none font-mono mt-2"
                        data-testid="kanban-erp-so-input"
                      />
                      {selectedOrder.erp_so_number && <p className="text-xs text-slate-400 mt-1">Current: {selectedOrder.erp_so_number}</p>}
                    </div>
                    {(newStatus === "dispatched" || selectedOrder.status === "packed") && (
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-red-600">ERP Invoice No. *</Label>
                        <Input
                          value={erpInvoiceNumber}
                          onChange={(e) => setErpInvoiceNumber(e.target.value)}
                          placeholder={selectedOrder.erp_invoice_number || "Required before dispatch"}
                          className="rounded-none font-mono mt-2 border-red-300"
                          data-testid="kanban-erp-invoice-input"
                        />
                        {!selectedOrder.erp_invoice_number && !erpInvoiceNumber && (
                          <p className="text-xs text-red-500 mt-1">Invoice number is mandatory to dispatch</p>
                        )}
                      </div>
                    )}
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Notes (Optional)</Label>
                      <Textarea value={statusNotes} onChange={(e) => setStatusNotes(e.target.value)} placeholder="Add notes..." className="rounded-none mt-2" rows={2} data-testid="kanban-status-notes" />
                    </div>
                    <Button onClick={updateOrderStatus} disabled={!newStatus || updatingStatus} className="bg-slate-900 text-white rounded-none btn-industrial" data-testid="kanban-update-status-btn">{updatingStatus ? "Updating..." : "Update Status"}</Button>
                  </div>
                </div>
              )}

              {/* Internal Comments - Admin Only */}
              {canManageOrders && (
                <div className="border border-slate-200 p-4">
                  <h3 className="font-heading text-sm uppercase tracking-wider mb-3 pb-2 border-b flex items-center gap-2">
                    <MessageSquare size={16} />
                    Internal Comments
                  </h3>
                  
                  {/* Existing Comments */}
                  <ScrollArea className="h-40 mb-4">
                    {selectedOrder.internal_comments?.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">No comments yet</p>
                    ) : (
                      <div className="space-y-3">
                        {selectedOrder.internal_comments?.map((comment, index) => (
                          <div key={index} className="bg-slate-50 p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{comment.user_name}</span>
                              <span className="text-xs text-slate-400">
                                {new Date(comment.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm">{comment.comment}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>

                  {/* Add Comment */}
                  <div className="flex gap-2">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add internal comment..."
                      className="rounded-none flex-1"
                      rows={2}
                      data-testid="comment-input"
                    />
                    <Button
                      onClick={addComment}
                      disabled={addingComment || !newComment.trim()}
                      className="bg-slate-900 text-white rounded-none"
                      data-testid="add-comment-button"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}

              {/* Actions */}
              {canManageOrders && ["packed", "dispatched", "delivered"].includes(selectedOrder.status) && (
                <div className="pt-4 border-t">
                  <Button
                    onClick={() => downloadChallan(selectedOrder.id)}
                    className="bg-slate-900 text-white rounded-none"
                    data-testid="download-challan-button"
                  >
                    <FileDown size={18} className="mr-2" />
                    Download Delivery Challan
                  </Button>
                </div>
              )}

              {/* Delivered Success */}
              {selectedOrder.status === "delivered" && (
                <div className="bg-green-50 border border-green-200 p-6 text-center">
                  <Truck className="w-12 h-12 text-green-600 mx-auto mb-2" />
                  <p className="font-heading text-lg uppercase tracking-wider text-green-800">
                    Order Delivered Successfully
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
